import React, { useState } from "react";
import { AlertTriangle, ChevronDown, Copy, Check, FileSpreadsheet } from "lucide-react";
import translations from "../common/Translations";
import { downloadFailedAccountsExcel } from "../../utils/failedAccountsExport";
import { fillTemplate } from "./bulkExportUtils";

/**
 * Expandable list of the account numbers a completed export could not render,
 * so the operator can retry exactly those (BLF-2171, item 7). The list can be
 * copied or pulled down as an Excel sheet to feed straight back into a retry.
 */
const FailedAccountsPanel = ({
  failedCount = 0,
  failedAccountNos = [],
  exportId = null,
  langMode,
  defaultOpen = false,
}) => {
  const t = translations[langMode] ?? {};
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [hasCopied, setHasCopied] = useState(false);

  const accounts = Array.isArray(failedAccountNos) ? failedAccountNos.filter(Boolean) : [];
  const count = Number(failedCount) || accounts.length;
  if (count <= 0) return null;

  const handleCopy = async () => {
    if (accounts.length === 0) return;
    try {
      await navigator.clipboard.writeText(accounts.join("\n"));
      setHasCopied(true);
      setTimeout(() => setHasCopied(false), 2000);
    } catch (error) {
      console.error("Error copying failed account numbers:", error);
    }
  };

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-3">
      <div className="flex items-start gap-2">
        <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-amber-800">
            {fillTemplate(
              t.bulkExportFailedAccountsTitle ?? "{count} account(s) could not be exported",
              { count }
            )}
          </p>
          {accounts.length > 0 && (
            <div className="mt-1.5 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setIsOpen((open) => !open)}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-800 hover:underline"
                aria-expanded={isOpen}
              >
                <ChevronDown size={12} className={isOpen ? "rotate-180 transition-transform" : "transition-transform"} />
                {isOpen
                  ? t.bulkExportHideAccounts ?? "Hide accounts"
                  : t.bulkExportShowAccounts ?? "Show accounts"}
              </button>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-800 hover:underline"
              >
                {hasCopied ? <Check size={12} /> : <Copy size={12} />}
                {hasCopied ? t.bulkExportCopied ?? "Copied" : t.bulkExportCopyAccounts ?? "Copy account numbers"}
              </button>
              <button
                type="button"
                onClick={() => downloadFailedAccountsExcel(accounts, exportId)}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-800 hover:underline"
              >
                <FileSpreadsheet size={12} />
                {t.bulkExportDownloadFailedExcel ?? "Download Excel"}
              </button>
            </div>
          )}
          {isOpen && accounts.length > 0 && (
            <ul className="mt-2 max-h-40 overflow-y-auto rounded-md border border-amber-200 bg-white p-2 text-[11px] text-slate-700">
              {accounts.map((accountNo) => (
                <li key={accountNo} className="rtl-keep-ltr py-0.5 font-mono">
                  {accountNo}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default FailedAccountsPanel;
